import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useOrganization } from '@/hooks/useOrganization';
import { useWebhooks, useDeleteWebhook, useUpdateWebhook, type Webhook } from '@/hooks/useWebhooks';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader } from '@/components/ui/loader';
import { PageHeader } from '@/components/layout/PageHeader';
import { WebhookDrawer } from '@/components/webhooks/WebhookDrawer';
import { WebhookIcon, PlusIcon, HomeIcon, BuildingIcon, EditIcon, TrashIcon, MoreHorizontal, ToggleLeftIcon, ToggleRightIcon } from 'lucide-react';
import { useToast } from '@/hooks/useToast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FeatureGate } from '@/components/features/FeatureGate';

export const Route = createFileRoute('/_authenticated/org/$orgId/webhooks')({
  component: OrganizationWebhooksPage,
});

function OrganizationWebhooksPage() {
  const { orgId } = Route.useParams();
  const { organization, isLoading } = useOrganization(orgId);
  const toast = useToast();

  const { data: webhooksData, isLoading: webhooksLoading, error: webhooksError } = useWebhooks(organization?.slug || orgId);
  const webhooks: Webhook[] = webhooksData?.webhooks || [];
  const deleteWebhookMutation = useDeleteWebhook(organization?.id || '');
  const updateWebhookMutation = useUpdateWebhook(organization?.id || '');
  
  const [showCreateDrawer, setShowCreateDrawer] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState<Webhook | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [webhookToDelete, setWebhookToDelete] = useState<Webhook | null>(null);
  
  if (webhooksError) {
    console.error('Error loading webhooks:', webhooksError);
  }

  const handleDelete = async () => {
    if (!webhookToDelete || !organization?.id) return;
    
    try {
      await deleteWebhookMutation.mutateAsync(webhookToDelete.id);
      toast.success('Webhook deleted successfully');
      setDeleteDialogOpen(false);
      setWebhookToDelete(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete webhook');
    }
  };

  const handleDeleteClick = (webhook: Webhook) => {
    setWebhookToDelete(webhook);
    setDeleteDialogOpen(true);
  };

  const handleToggleActive = async (webhook: Webhook) => {
    if (!organization?.id) return;
    
    try {
      await updateWebhookMutation.mutateAsync({
        organizationId: organization.id,
        webhookId: webhook.id,
        isActive: !webhook.isActive,
      });
      toast.success(`Webhook ${webhook.isActive ? 'deactivated' : 'activated'} successfully`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update webhook');
    }
  };

  const handleEditClick = (webhook: Webhook) => {
    setEditingWebhook(webhook);
    setShowCreateDrawer(true);
  };

  if (isLoading || webhooksLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader variant="spinner" size="lg" color="accent" text="Loading webhooks..." />
      </div>
    );
  }

  const breadcrumbs = [
    {
      name: 'Dashboard',
      to: '/dashboard',
      icon: HomeIcon,
    },
    {
      name: organization?.name || 'Organization',
      to: `/org/${organization?.slug || orgId}`,
      icon: BuildingIcon,
    },
    {
      name: 'Webhooks',
      to: `/org/${organization?.slug || orgId}/webhooks`,
    },
  ];

  return (
    <FeatureGate
      organizationId={orgId}
      featureKey="webhooks"
      featureName="Webhooks"
      description="Webhooks allow you to receive real-time events from your organization. Upgrade your plan to access this feature."
    >
      <div className="space-y-6">
        <PageHeader
          breadcrumbs={breadcrumbs}
          title="Webhooks"
          subtitle={organization ? `Manage outgoing webhooks for ${organization.name}` : undefined}
          action={
            <Button 
              onClick={() => {
                setEditingWebhook(null);
                setShowCreateDrawer(true);
              }}
              disabled={!organization?.id}
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Create Webhook
            </Button>
          }
        />

        <Card>
        <CardHeader>
          <CardTitle>Webhooks</CardTitle>
          <CardDescription>
            Configure webhooks to receive real-time events from your organization
          </CardDescription>
        </CardHeader>
        <CardContent>
          {webhooks.length > 0 ? (
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>URL</TableHead>
                    <TableHead>Events</TableHead>
                    <TableHead>Auth Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {webhooks.map((webhook) => (
                    <TableRow key={webhook.id}>
                      <TableCell className="font-medium">{webhook.name}</TableCell>
                      <TableCell>
                        <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                          {webhook.url}
                        </code>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {webhook.events.slice(0, 2).map((event) => (
                            <Badge key={event} variant="secondary" className="text-xs">
                              {event === '*' ? 'All' : event.split('.')[0]}
                            </Badge>
                          ))}
                          {webhook.events.length > 2 && (
                            <Badge variant="secondary" className="text-xs">
                              +{webhook.events.length - 2}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {webhook.authType.toUpperCase()}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={webhook.isActive ? 'default' : 'secondary'}>
                          {webhook.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEditClick(webhook)}>
                              <EditIcon className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleToggleActive(webhook)}>
                              {webhook.isActive ? (
                                <>
                                  <ToggleLeftIcon className="w-4 h-4 mr-2" />
                                  Deactivate
                                </>
                              ) : (
                                <>
                                  <ToggleRightIcon className="w-4 h-4 mr-2" />
                                  Activate
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDeleteClick(webhook)}
                              className="text-red-600"
                            >
                              <TrashIcon className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <WebhookIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No webhooks yet</h3>
              <p className="text-gray-500 mb-4">
                Create your first webhook to receive real-time events
              </p>
              <Button
                onClick={() => {
                  setEditingWebhook(null);
                  setShowCreateDrawer(true);
                }}
                disabled={!organization?.id}
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Create Webhook
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Webhook Drawer */}
      {organization?.id && (
        <WebhookDrawer
          organizationId={organization.id}
          webhook={editingWebhook || undefined}
          open={showCreateDrawer}
          onOpenChange={(open) => {
            setShowCreateDrawer(open);
            if (!open) {
              setEditingWebhook(null);
            }
          }}
          onSuccess={() => {
            setEditingWebhook(null);
          }}
        />
      )}

      {/* Delete Webhook Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Webhook</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{webhookToDelete?.name || 'this webhook'}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setWebhookToDelete(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteWebhookMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteWebhookMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </FeatureGate>
  );
}

